import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { DeleteModal } from "../../components";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import { MagnifyingGlass, PencilSimple, Trash, AddressBook } from "@phosphor-icons/react";
import {
  PageHeader, Input, Table, THead, TBody, TR, TH, TD,
  Skeleton, EmptyState, Pagination, Tooltip,
} from "../../components/ui";
import EditContactSidebar from "./EditContactSidebar";

function Contacts() {
  const { checkAccess } = useAccessChecker();

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [contactIdToDelete, setContactIdToDelete] = useState(null);
  const [isEditSidebarOpen, setIsEditSidebarOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    getContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchValue]);

  const getContacts = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(`/contacts/search?page=${currentPage}&per_page=10`, {
        searchValue: searchValue,
      });
      const responseData = response.data;
      if (responseData) {
        setContacts(responseData.data);
        setCurrentPage(responseData.current_page);
        setTotalPages(responseData.last_page);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error fetching contacts");
    } finally {
      setLoading(false);
    }
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setIsEditSidebarOpen(true);
  };

  const handleContactUpdated = (updatedContact) => {
    setContacts((prev) => prev.map((c) => (c.id === updatedContact.id ? updatedContact : c)));
    getContacts();
  };

  const handleDeleteContact = (contactId) => {
    setContactIdToDelete(contactId);
    setDeleteModal(true);
  };

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1);
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" });

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title="Contacts"
        subtitle="Messages submitted through your contact form."
        actions={
          <Input
            icon={MagnifyingGlass}
            placeholder="Search contacts…"
            value={searchValue}
            onChange={handleSearch}
            containerClassName="w-full sm:w-72"
          />
        }
      />

      <Table>
        <THead sticky>
          <TR className="hover:bg-transparent">
            <TH>Name</TH>
            <TH>Email</TH>
            <TH>Phone</TH>
            <TH>Message</TH>
            <TH>Date</TH>
            <TH className="text-right">Actions</TH>
          </TR>
        </THead>
        <TBody>
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <TR key={i} className="hover:bg-transparent">
                {Array.from({ length: 6 }).map((__, j) => (
                  <TD key={j}><Skeleton className="h-3.5 w-full max-w-[150px]" /></TD>
                ))}
              </TR>
            ))}

          {!loading &&
            contacts.map((contact) => (
              <TR key={contact.id}>
                <TD className="font-medium text-ink">
                  {contact.firstName} {contact.lastName}
                </TD>
                <TD className="text-muted">{contact.email}</TD>
                <TD className="text-muted">{contact.phone}</TD>
                <TD className="text-muted">
                  <span className="block max-w-[420px] truncate">{contact.message}</span>
                </TD>
                <TD className="text-muted">{formatDate(contact.created_at)}</TD>
                <TD>
                  <div className="flex items-center justify-end gap-0.5">
                    {checkAccess("contacts", "update") && (
                      <Tooltip label="Edit">
                        <button onClick={() => handleEditContact(contact)} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink">
                          <PencilSimple size={16} />
                        </button>
                      </Tooltip>
                    )}
                    {checkAccess("contacts", "delete") && (
                      <Tooltip label="Delete">
                        <button onClick={() => handleDeleteContact(contact.id)} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600">
                          <Trash size={16} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </TD>
              </TR>
            ))}

          {!loading && contacts.length === 0 && (
            <tr>
              <td colSpan={6}>
                <EmptyState
                  icon={AddressBook}
                  title="No contacts found"
                  description="Try adjusting your search, or check back later for new messages."
                />
              </td>
            </tr>
          )}
        </TBody>
      </Table>

      <Pagination
        className="mt-4"
        page={currentPage}
        totalPages={totalPages}
        onChange={(p) => setCurrentPage(p)}
        summary={`Page ${currentPage} of ${totalPages}`}
      />

      <EditContactSidebar
        isOpen={isEditSidebarOpen}
        onClose={() => setIsEditSidebarOpen(false)}
        contact={selectedContact}
        onContactUpdated={handleContactUpdated}
      />
      <DeleteModal
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={contactIdToDelete}
        setDeleteId={setContactIdToDelete}
        Model="contacts"
        setDone={() => getContacts()}
        setLoding={setLoading}
      />
    </div>
  );
}

export default Contacts;
